/**
 * Pestaña "Firmas" en la ficha del contrato.
 * Muestra las firmas manuscritas capturadas durante el hiring flow (paso 3).
 * Las firmas se obtienen desde workflow-progress del backend.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  PencilSquareIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { contractsService } from '@/services/contractsService';

interface SignerData {
  id: string;
  name?: string;
  signed: boolean;
  signed_at?: string;
  signature_base64?: string;
}

interface SignaturesData {
  signers: SignerData[];
  accepted_terms: boolean;
  completed: boolean;
  completed_at?: string;
}

interface Props {
  hiringCode: string;
  clientName?: string;
  contractType?: string;
}

function getSignerLabel(id: string, index: number): string {
  if (id === 'primary' || index === 0) return 'Titular principal';
  if (id === 'secondary' || index === 1) return 'Segundo titular';
  return `Titular ${index + 1}`;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function ContractSignaturesTab({ hiringCode, clientName, contractType }: Props) {
  const [signatures, setSignatures] = useState<SignaturesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadSignatures = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await contractsService.getContractSignatures(hiringCode);
      setSignatures(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [hiringCode]);

  useEffect(() => {
    loadSignatures();
  }, [loadSignatures]);

  // Estado de carga
  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <PencilSquareIcon className="w-8 h-8 text-gray-300" />
            <span className="text-sm">Cargando firmas...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error
  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <ExclamationTriangleIcon className="w-8 h-8 mx-auto text-amber-400 mb-2" />
          <p className="text-sm">No se pudieron cargar las firmas.</p>
          <button
            onClick={loadSignatures}
            className="mt-2 text-sm text-green-600 hover:underline"
          >
            Reintentar
          </button>
        </CardContent>
      </Card>
    );
  }

  // Sin firmas
  const signers = signatures?.signers ?? [];
  const signedSigners = signers.filter(s => s.signed && s.signature_base64);

  if (!signatures || signedSigners.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PencilSquareIcon className="w-5 h-5" />
            Firmas Manuscritas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <PencilSquareIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 font-medium">No hay firmas registradas</p>
            <p className="text-xs text-gray-400 mt-1">
              Las firmas se capturan cuando el cliente completa el paso de firma del contrato.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Firmas encontradas
  const isDuo = contractType === 'duo' || contractType === 'family' || signers.length > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PencilSquareIcon className="w-5 h-5" />
          Firmas Manuscritas
          <Badge variant="outline" className="ml-auto text-xs">
            {signedSigners.length} de {isDuo ? signers.length : 1} firma{signedSigners.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Resumen */}
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
          {signatures.accepted_terms && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircleIcon className="w-3 h-3" />
              Términos aceptados
            </Badge>
          )}
          {signatures.completed && signatures.completed_at && (
            <Badge variant="secondary" className="gap-1">
              <ClockIcon className="w-3 h-3" />
              Completado {formatDate(signatures.completed_at)}
            </Badge>
          )}
        </div>

        {/* Grid de firmas */}
        <div className={`grid gap-4 ${isDuo ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
          {signers.map((signer, idx) => (
            <div
              key={signer.id}
              className={`
                border rounded-lg p-4 transition-colors
                ${signer.signed && signer.signature_base64
                  ? 'border-green-200 bg-green-50/50'
                  : 'border-gray-200 bg-gray-50/50'}
              `}
              data-testid={`signature-card-${signer.id}`}
            >
              {/* Header del firmante */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {signer.name || clientName || getSignerLabel(signer.id, idx)}
                  </span>
                  <Badge
                    variant={signer.id === 'primary' || idx === 0 ? 'default' : 'secondary'}
                    className="ml-2 text-[10px]"
                  >
                    {getSignerLabel(signer.id, idx)}
                  </Badge>
                </div>
                {signer.signed ? (
                  <Badge className="bg-green-100 text-green-800 text-[10px]">
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    Firmado
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 text-[10px]">
                    Pendiente
                  </Badge>
                )}
              </div>

              {/* Imagen de la firma */}
              {signer.signature_base64 ? (
                <div className="bg-white border border-gray-200 rounded-md p-2 mb-2">
                  <img
                    src={signer.signature_base64}
                    alt={`Firma de ${signer.name || getSignerLabel(signer.id, idx)}`}
                    className="w-full h-auto max-h-32 object-contain"
                    draggable={false}
                  />
                </div>
              ) : (
                <div className="bg-white border border-dashed border-gray-300 rounded-md p-6 mb-2 text-center">
                  <PencilSquareIcon className="w-6 h-6 mx-auto text-gray-300" />
                  <p className="text-xs text-gray-400 mt-1">Sin firma</p>
                </div>
              )}

              {/* Fecha de firma */}
              {signer.signed_at && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <ClockIcon className="w-3 h-3" />
                  {formatDate(signer.signed_at)}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
